import { ReactNode } from 'react';

import { attackSurfaceOptions } from '@/app/constant';
import { BaseTag, RiskTag, TagSeverity } from '@/components/chariot-ui';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import {
  Access,
  EntityHistory,
  RiskSeverity,
  RiskStatus,
  RiskStatusLabel,
  RiskStatusWithoutSeverity,
  UserType,
} from '@/types';
import { formatDate } from '@/utils/date.util';
import { getAccessEmail } from '@/utils/getAccessEmail';
import { formatAttackSurfaceLabel } from '@/utils/mappings.util';
import { getMachineRiskComment } from '@/utils/risk.util';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import { maskPraetorianName, stripAgentTags } from '@/utils/userDisplay.util';

const EmptySpace = ' ';

function isLikelyMarkdown(text: string | null | undefined): boolean {
  if (!text) return false;
  // This regex looks for common markdown patterns.
  // It's a heuristic and might not be perfect.
  const markdownRegex = new RegExp(
    [
      '(^|\\n)#{1,6}\\s', // Headers
      '(^|\\n)[\\*\\-\\+]\\s', // Unordered list items
      '(^|\\n)\\d+\\.\\s', // Ordered list items
      '(^|\\n)>\\s', // Blockquotes
      '```', // Code blocks (start or end)
      '!\\[[^\\]]*\\]\\([^\\)]*\\)', // Images ![alt](url)
      '\\[[^\\]]+\\]\\([^\\)]+\\)', // Links [text](url)
      '\\*\\*([^\\*\\*]+)\\*\\*', // Bold **text** (non-empty)
      '__([^__]+)__', // Bold __text__ (non-empty)
      '`([^`]+)`', // Inline code `text` (non-empty)
    ].join('|'),
    'gm' // global for line starts (^), multiline for \n
  );
  return markdownRegex.test(text);
}

interface HistoryDiffResult {
  title: ReactNode;
  updated: string;
}

const severityMap: Record<RiskSeverity, TagSeverity> = {
  [RiskSeverity.Critical]: 'critical',
  [RiskSeverity.High]: 'high',
  [RiskSeverity.Medium]: 'medium',
  [RiskSeverity.Low]: 'low',
  [RiskSeverity.Exposure]: 'exposure',
  [RiskSeverity.Info]: 'info',
};

// Full read vocabulary of attack-surface tokens (includes the read/display-only
// `llm`), derived from the shared `attackSurfaceOptions` so the Timeline gate
// can't drift from the rest of the surface UI. Used only to *detect* a
// surface-transition audit record; unknown tokens fall through to the existing
// status/severity/comment behavior so no other record shape is affected.
const ATTACK_SURFACE_VALUES = new Set(
  attackSurfaceOptions
    .map(option => option.value)
    .filter((value): value is string => typeof value === 'string')
);

const parseSurfaceTokens = (value: unknown): string[] =>
  String(value ?? '')
    .split(',')
    .map(token => token.trim().toLowerCase())
    .filter(Boolean);

export function getHistoryDiff(
  historyItem: EntityHistory,
  isFirst: boolean,
  accesss: Access[] = [],
  userType: UserType
): HistoryDiffResult {
  if (isFirst) {
    const toStatus = historyItem.to as string;
    if (!toStatus) {
      return { title: '', updated: '' };
    }

    const { severity: toSeverity } = getRiskStatusLabel(toStatus);
    if (!toSeverity) {
      return { title: '', updated: '' };
    }

    const result = {
      title: (
        <div className="whitespace-break-spaces">
          <p className="inline">
            <strong>First Tracked</strong>
            {EmptySpace}as{EmptySpace}
          </p>
          <RiskTag severity={severityMap[toSeverity]} size="sm" />
        </div>
      ),
      updated: formatDate(historyItem.updated),
    };
    return result;
  }

  const { status: statusFrom, severity: severityFrom } = getRiskStatusLabel(
    historyItem.from
  );
  const { status: statusTo, severity: severityTo } = getRiskStatusLabel(
    historyItem.to
  );
  const isStatusWithoutSeverityFrom = RiskStatusWithoutSeverity.includes(
    historyItem.from as RiskStatus
  );

  const isStatusChanged = statusFrom !== statusTo;
  const isSeverityChanged = severityFrom !== severityTo;
  const isBothChanged = isSeverityChanged && isStatusChanged;

  const by = getAccessEmail(accesss, historyItem?.by);
  const displayBy = by
    ? maskPraetorianName(by, undefined, userType)
    : undefined;

  // Attack-surface audit records: the backend writes surface changes with
  // `from`/`to` as comma-joined lowercase surface tokens (e.g. "internal",
  // "internal, external", or "" for the first classification) and no comment.
  // These parse as neither a status nor a severity change, so without this
  // branch getHistoryDiff returns title:null and Timeline omits the row.
  // Detect a surface transition ONLY when the existing status/severity
  // detection found nothing, `to` is non-empty, and EVERY token of both `from`
  // (if present) and `to` is a member of the surface vocabulary — any unknown
  // token falls through to the existing behavior, so status codes, severity
  // codes, comment-only records, and asset history are unaffected.
  const toSurfaces = parseSurfaceTokens(historyItem.to);
  const fromSurfaces = parseSurfaceTokens(historyItem.from);
  const isSurfaceTransition =
    !isStatusChanged &&
    !isSeverityChanged &&
    toSurfaces.length > 0 &&
    [...fromSurfaces, ...toSurfaces].every(token =>
      ATTACK_SURFACE_VALUES.has(token)
    );

  if (isSurfaceTransition) {
    const hasFrom = fromSurfaces.length > 0;
    const fromLabel = fromSurfaces.map(formatAttackSurfaceLabel).join(', ');
    const toLabel = toSurfaces.map(formatAttackSurfaceLabel).join(', ');

    return {
      title: (
        <div className="leading-7 whitespace-break-spaces">
          {displayBy && <p className="text-sm font-bold">{displayBy}</p>}
          <p className="inline">
            {displayBy ? (
              <span>
                {hasFrom ? 'Changed the ' : 'Set the '}
                <span className="font-semibold">Attack Surface</span>
                {hasFrom ? ' from' : ' to'}
              </span>
            ) : (
              <span>
                <span className="font-semibold">Attack Surface</span>
                {hasFrom ? ' changed from' : ' set to'}
              </span>
            )}
            {EmptySpace}
          </p>
          {hasFrom && (
            <>
              <BaseTag size="sm">{fromLabel}</BaseTag>
              <p className="inline-block">
                {EmptySpace}to{EmptySpace}
              </p>
            </>
          )}
          <BaseTag size="sm">{toLabel}</BaseTag>
        </div>
      ),
      updated: formatDate(historyItem.updated),
    };
  }

  const severity = (
    <>
      <p className="inline">
        {displayBy ? (
          <span>
            Changed the <span className="font-semibold">Severity</span>{' '}
            {isStatusWithoutSeverityFrom ? 'to' : 'from'}
          </span>
        ) : (
          <span>
            <span className="font-semibold">Severity</span>
            {isStatusWithoutSeverityFrom ? ' set to' : ' changed from'}
          </span>
        )}
        {EmptySpace}
      </p>
      {!isStatusWithoutSeverityFrom && severityFrom && (
        <>
          <RiskTag severity={severityMap[severityFrom]} size="sm" />
          <p className="inline">
            {EmptySpace}to{EmptySpace}
          </p>
        </>
      )}
      {severityTo && <RiskTag severity={severityMap[severityTo]} size="sm" />}
    </>
  );

  const status = (
    <>
      <p className="inline">
        {isBothChanged ? (
          <>
            {EmptySpace}, and <span className="font-semibold">Status</span> from
            {EmptySpace}
          </>
        ) : (
          <>
            {displayBy ? (
              <span>
                Changed the <span className="font-semibold">Status</span> from
              </span>
            ) : (
              <span>
                <span className="font-semibold">Status</span> changed from
              </span>
            )}
            {EmptySpace}
          </>
        )}
      </p>
      <BaseTag size="sm">
        {RiskStatusLabel[historyItem?.from?.[0] as RiskStatus] ?? 'Unknown'}
      </BaseTag>
      <p className="inline-block">
        {EmptySpace}to{EmptySpace}
      </p>
      <BaseTag size="sm">
        {RiskStatusLabel[historyItem?.to?.[0] as RiskStatus] ?? 'Unknown'}
      </BaseTag>
    </>
  );

  const machineCommentNode = getMachineRiskComment(historyItem)?.comment;
  let displayedCommentContent: ReactNode = null;

  if (machineCommentNode) {
    displayedCommentContent = machineCommentNode;
  } else if (historyItem.comment) {
    const commentString = stripAgentTags(String(historyItem.comment), userType);
    if (commentString.trim()) {
      if (isLikelyMarkdown(commentString)) {
        displayedCommentContent = (
          <div className="-mb-4">
            <MarkdownPreview source={commentString?.trim()} />
          </div>
        );
      } else {
        // Render as plain text, similar to original approach with quotes and word-break
        displayedCommentContent = (
          <i
            style={{ wordBreak: 'break-word' }}
          >{` "${commentString?.trim()}"`}</i>
        );
      }
    }
  }

  const comment = displayedCommentContent ? (
    isStatusChanged || isSeverityChanged ? (
      <p className="inline">
        {` with `}
        <span className="font-semibold">Note</span>
        {EmptySpace}
        {displayedCommentContent}
      </p>
    ) : (
      <p className="inline">
        <span>
          {'Added the'}
          <span className="font-semibold">
            {'\xa0'}Note{'\xa0'}
          </span>
          {displayedCommentContent}
        </span>
      </p>
    )
  ) : null;

  const hasChange =
    isStatusChanged || isSeverityChanged || !!displayedCommentContent;

  return {
    title: hasChange ? (
      <div className="leading-7 whitespace-break-spaces">
        {displayBy && <p className="text-sm font-bold">{displayBy}</p>}
        {(isSeverityChanged || isBothChanged) && severity}
        {(isStatusChanged || isBothChanged) && status}
        {comment}
      </div>
    ) : null,
    updated: formatDate(historyItem.updated),
  };
}
